import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import ThemeProvider from '@softwaredev_cegeka/stix-core/dist/components/ThemeProvider'
import AuthApi from '@softwaredev_cegeka/auth-api'
import ccpTheme from 'src/themes/ccpTheme'
import makeLanguageAware from 'src/utils/makeLanguageAware'
import logoEn from 'src/assets/images/logo-horizon-en.svg'
import logoNl from 'src/assets/images/logo-horizon-nl.svg'
import setFavicon from 'src/utils/setFavicon'
import isCcpTheme from 'src/utils/isCcpTheme'
import { getUserData } from 'src/HOCs/withAuth'
import {
    CEGEKA_ORGANIZATION_ID,
    getCurrentOrganizationId,
} from 'src/utils/permissions'

const authApi = new AuthApi()

/**
 * ActiveThemeWrapper is a React component that manages and provides the active theme for its children.
 * It fetches the current organization's theme, applies custom images for non-Cegeka customers,
 * and falls back to a default theme if fetching fails. It also supports language-specific logo variations.
 * @component
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The child components to be wrapped with the active theme.
 * @returns {React.ReactElement|null} The themed children wrapped in a ThemeProvider, or null while loading.
 */
export default function ActiveThemeWrapper(props) {
    const { children } = props
    const [activeTheme, setActiveTheme] = useState(null)
    const userData = getUserData()
    const organizationId = getCurrentOrganizationId(userData)
    const isOrgReady = !!organizationId

    const fetchActiveTheme = useCallback(async () => {
        try {
            const theme = await authApi.fetchTheme()
            return Promise.resolve(theme)
        } catch (e) {
            console.error(e)
            return Promise.reject(e)
        }
    }, [])

    const loadTheme = useCallback(async () => {
        try {
            const { data = {} } = await fetchActiveTheme()
            const isMatchingOrganization =
                String(data.organization_id) === String(organizationId)

            if (isMatchingOrganization && data?.css_definition) {
                setActiveTheme(prevTheme => ({
                    ...prevTheme,
                    name: data.organization_id,
                    variables: data.css_definition,
                }))
            }
            // Pass custom images. Only for non Cegeka customers.
            if (
                isMatchingOrganization &&
                data?.images?.length > 0 &&
                data?.organization_id !== CEGEKA_ORGANIZATION_ID
            ) {
                const imageConfig = data.images.reduce(
                    (acc, { key, download_url }) => {
                        acc[key] = `/api/auth/${download_url}`
                        return acc
                    },
                    {}
                )
                setActiveTheme(prevTheme => ({
                    ...prevTheme,
                    config: imageConfig,
                }))
            } else {
                setActiveTheme(ccpTheme)
            }
        } catch (error) {
            console.error(error)
            // Theme call failed, fallback to ccp theme.
            setActiveTheme(ccpTheme)
        }
    }, [fetchActiveTheme, organizationId])

    useEffect(() => {
        // We only support language-specific logo variations for the original logo.
        if (!activeTheme?.config?.applicationLogo || !isCcpTheme(activeTheme)) {
            return
        }
        makeLanguageAware(
            activeTheme.config,
            'logo',
            (_originalValue, language) => {
                switch (language) {
                    case 'nl':
                        return logoNl
                    case 'en':
                    default:
                        return logoEn
                }
            }
        )
    }, [activeTheme, organizationId])

    useEffect(() => {
        if (!isOrgReady) {
            return
        }
        loadTheme()
        setFavicon(activeTheme?.config?.shareLogo ?? '/favicon.ico')
    }, [loadTheme, activeTheme?.config?.shareLogo, organizationId, isOrgReady])

    if (!isOrgReady || !activeTheme) {
        return null
    }
    return <ThemeProvider theme={activeTheme}>{children}</ThemeProvider>
}

ActiveThemeWrapper.propTypes = {
    children: PropTypes.node,
}
